import React from 'react'
import { Loader2, Inbox, X, ChevronLeft, ChevronRight } from 'lucide-react'

export function Card({ children, style, ...rest }) {
  return (
    <div
      style={{
        background: 'var(--surface)',
        borderRadius: 'var(--radius)',
        border: '1px solid var(--border)',
        boxShadow: 'var(--shadow-sm)',
        ...style
      }}
      {...rest}
    >
      {children}
    </div>
  )
}

const buttonVariants = {
  primary: { background: 'var(--brand)', color: '#fff', border: '1px solid var(--brand)' },
  soft: { background: 'var(--brand-soft)', color: 'var(--brand-soft-text)', border: '1px solid transparent' },
  success: { background: 'var(--success)', color: '#fff', border: '1px solid var(--success)' },
  danger: { background: 'var(--danger)', color: '#fff', border: '1px solid var(--danger)' },
  'danger-soft': { background: 'var(--danger-soft)', color: 'var(--danger)', border: '1px solid transparent' },
  ghost: { background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)' },
  neutral: { background: '#f2f2f5', color: 'var(--text)', border: '1px solid transparent' },
}

export function Button({ variant = 'primary', size = 'md', icon: Icon, loading, disabled, children, style, ...rest }) {
  const v = buttonVariants[variant] || buttonVariants.primary
  const pad = size === 'sm' ? '7px 12px' : '10px 18px'
  const fontSize = size === 'sm' ? 12.5 : 13.5
  return (
    <button
      disabled={disabled || loading}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
        padding: pad, borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        opacity: disabled || loading ? 0.6 : 1,
        transition: 'filter 0.12s, transform 0.05s',
        whiteSpace: 'nowrap',
        ...v,
        ...style
      }}
      onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.98)' }}
      onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)' }}
      {...rest}
    >
      {loading ? <Loader2 size={14} className="spin" /> : Icon ? <Icon size={14} /> : null}
      {children}
    </button>
  )
}

const fieldBase = {
  width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border-strong)', fontSize: 13.5, color: 'var(--text)',
  background: 'var(--surface)', outline: 'none', transition: 'border-color 0.12s, box-shadow 0.12s'
}

function focusable(e) {
  e.target.style.borderColor = 'var(--brand)'
  e.target.style.boxShadow = '0 0 0 3px var(--brand-soft)'
}
function blurable(e) {
  e.target.style.borderColor = 'var(--border-strong)'
  e.target.style.boxShadow = 'none'
}

export function Input({ style, ...rest }) {
  return <input style={{ ...fieldBase, ...style }} onFocus={focusable} onBlur={blurable} {...rest} />
}

export function Textarea({ style, ...rest }) {
  return <textarea style={{ ...fieldBase, resize: 'vertical', fontFamily: 'inherit', ...style }} onFocus={focusable} onBlur={blurable} {...rest} />
}

export function Select({ style, children, ...rest }) {
  return <select style={{ ...fieldBase, cursor: 'pointer', ...style }} onFocus={focusable} onBlur={blurable} {...rest}>{children}</select>
}

export function Label({ children, style }) {
  return (
    <label style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6, letterSpacing: 0.2, textTransform: 'uppercase', ...style }}>
      {children}
    </label>
  )
}

export function Field({ label, children, style }) {
  return (
    <div style={style}>
      {label && <Label>{label}</Label>}
      {children}
    </div>
  )
}

const badgeTones = {
  success: { background: 'var(--success-soft)', color: 'var(--success)' },
  warning: { background: 'var(--warning-soft)', color: 'var(--warning)' },
  danger: { background: 'var(--danger-soft)', color: 'var(--danger)' },
  brand: { background: 'var(--brand-soft)', color: 'var(--brand-soft-text)' },
  neutral: { background: '#f0f0f3', color: 'var(--text-muted)' },
}

export function Badge({ tone = 'neutral', children, style }) {
  const t = badgeTones[tone] || badgeTones.neutral
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 600,
      ...t, ...style
    }}>
      {children}
    </span>
  )
}

export function Dot({ on }) {
  return (
    <span style={{
      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
      background: on ? 'var(--success)' : 'var(--danger)',
      boxShadow: on ? '0 0 0 3px var(--success-soft)' : '0 0 0 3px var(--danger-soft)'
    }} />
  )
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
      <div>
        <h1 style={{ fontSize: 21, fontWeight: 700, color: 'var(--text)', letterSpacing: -0.3 }}>{title}</h1>
        {subtitle && <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 3 }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>{actions}</div>}
    </div>
  )
}

export function EmptyState({ icon: Icon = Inbox, title = 'Tidak ada data', hint }) {
  return (
    <div style={{ textAlign: 'center', padding: '56px 20px', color: 'var(--text-faint)' }}>
      <Icon size={34} strokeWidth={1.5} style={{ marginBottom: 10, opacity: 0.7 }} />
      <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-muted)' }}>{title}</div>
      {hint && <div style={{ fontSize: 12, marginTop: 4 }}>{hint}</div>}
    </div>
  )
}

export function Banner({ tone = 'brand', children }) {
  const t = badgeTones[tone] || badgeTones.brand
  return (
    <div style={{
      background: t.background, color: t.color, border: `1px solid ${t.background}`,
      borderRadius: 'var(--radius-sm)', padding: '11px 16px', fontSize: 13, marginBottom: 20,
      display: 'flex', alignItems: 'center', gap: 8
    }}>
      {children}
    </div>
  )
}

export function Alert({ type = 'success', children }) {
  const tone = type === 'success' ? 'success' : 'danger'
  const t = badgeTones[tone]
  return (
    <div style={{ background: t.background, color: t.color, padding: '10px 14px', borderRadius: 'var(--radius-sm)', fontSize: 13, marginBottom: 16, fontWeight: 500 }}>
      {children}
    </div>
  )
}

export function Avatar({ label, size = 42, gradient = true }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: gradient ? 'linear-gradient(135deg, var(--brand), #7c8cf8)' : 'var(--brand-soft)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: gradient ? '#fff' : 'var(--brand-soft-text)', fontWeight: 700, fontSize: size * 0.4
    }}>
      {label}
    </div>
  )
}

export function Modal({ children, onClose, width = 420 }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(16,16,24,0.5)', backdropFilter: 'blur(2px)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 200,
      padding: 'clamp(16px, 6vw, 48px) 14px', overflow: 'auto'
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--surface)', borderRadius: 'var(--radius-lg)', padding: 'clamp(18px, 5vw, 28px)',
        width, maxWidth: '100%', boxShadow: 'var(--shadow-lg)'
      }}>
        {children}
      </div>
    </div>
  )
}

export function ModalHeader({ title, subtitle, onClose }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
      <div>
        <h2 style={{ fontSize: 16.5, fontWeight: 700, color: 'var(--text)' }}>{title}</h2>
        {subtitle && <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>{subtitle}</p>}
      </div>
      {onClose && (
        <button onClick={onClose} style={{
          background: '#f2f2f5', border: 'none', borderRadius: 8, width: 28, height: 28,
          display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-muted)'
        }}><X size={15} /></button>
      )}
    </div>
  )
}

export function Table({ children }) {
  return (
    <Card style={{ overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>{children}</table>
      </div>
    </Card>
  )
}

export function THead({ cols }) {
  return (
    <thead>
      <tr style={{ background: '#fafafc' }}>
        {cols.map(h => (
          <th key={h} style={{
            padding: '11px 16px', textAlign: 'left', fontWeight: 600, fontSize: 11.5,
            color: 'var(--text-muted)', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: 0.3,
            borderBottom: '1px solid var(--border)'
          }}>{h}</th>
        ))}
      </tr>
    </thead>
  )
}

export const td = { padding: '12px 16px', borderTop: '1px solid var(--border)' }

export function Pagination({ page, limit, total, onChange }) {
  const totalPages = Math.max(1, Math.ceil(total / limit))
  const from = total === 0 ? 0 : (page - 1) * limit + 1
  const to = Math.min(page * limit, total)
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, padding: '12px 16px', borderTop: '1px solid var(--border)', fontSize: 12.5, color: 'var(--text-muted)' }}>
      <span>{total === 0 ? 'Tidak ada data' : `Menampilkan ${from}–${to} dari ${total}`}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Button size="sm" variant="ghost" icon={ChevronLeft}
          disabled={page <= 1} onClick={() => onChange(page - 1)}>Sebelumnya</Button>
        <span style={{ fontWeight: 600 }}>Halaman {page} / {totalPages}</span>
        <Button size="sm" variant="ghost" icon={ChevronRight}
          disabled={page >= totalPages} onClick={() => onChange(page + 1)}>Berikutnya</Button>
      </div>
    </div>
  )
}

export function Tabs({ tabs, active, onChange }) {
  return (
    <div style={{ overflowX: 'auto', marginBottom: 16, maxWidth: '100%' }}>
      <div style={{ display: 'inline-flex', gap: 2, background: '#f0f0f3', padding: 3, borderRadius: 'var(--radius-sm)' }}>
        {tabs.map(([key, label, Icon]) => (
          <button key={key} onClick={() => onChange(key)} style={{
            padding: '7px 16px', border: 'none', borderRadius: 7, cursor: 'pointer',
            fontSize: 12.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
            whiteSpace: 'nowrap',
            background: active === key ? '#fff' : 'transparent',
            color: active === key ? 'var(--text)' : 'var(--text-muted)',
            boxShadow: active === key ? 'var(--shadow-sm)' : 'none'
          }}>
            {Icon && <Icon size={13} />}{label}
          </button>
        ))}
      </div>
    </div>
  )
}
